import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createReducer } from 'react-use';
import { analyticsMiddleware } from './analytics-middleware';
import { createInitialState, defaultState, reducer } from './data-reducer';
import { favoriteStorageMiddleware } from './favorite-storage-middleware';
import { fetchMiddleware } from './fetch-middleware';
import { personalizationDialogMiddleware } from './personalization-dialog-middleware';
import { personalizationStorageMiddleware } from './personalization-storage-middleware';
import { rtsLoginDialogMiddleware } from './rts-login-dialog-middleware';
import { rtsWatchLaterStorageMiddleware } from './rts-watch-later-storage-middleware';
import { snackbarMiddleware } from './snackbar-middleware';
import { useRtsLogin } from './use-rts-login';
import { watchLaterStorageMiddleware } from './watch-later-storage-middleware';
import { useAnalyticsDispatch } from '../../analytics/AnalyticsContext';
import { useDialogDispatch } from '../../components/Dialog/DialogContext';
import { useSnackbarDispatch } from '../../components/Snackbar/SnackbarContext';
import { usePath, useRoutes, useVideoConfig } from '../../config/VideoAppContext';
import { useAxios } from '../../hooks/use-axios';
import { useHydrated } from '../../hooks/use-hydrated';
import { channelHistoryStorage } from '../../storage/channel-history-storage';
import { favoriteGuideStorage } from '../../storage/favorite-guide-storage';
import { favoriteShowsStorage } from '../../storage/favorite-shows-storage';
import { favoriteTopicsStorage } from '../../storage/favorite-topics-storage';
import { personalizationSettings } from '../../storage/personalization-settings';
import { subtitleStorage } from '../../storage/subtitle-storage';
import { userIdStorage } from '../../storage/user-id-storage';
import { watchLaterStorage } from '../../storage/watch-later-storage';
import { jsx as _jsx } from "react/jsx-runtime";
export const DataStateContext = /*#__PURE__*/React.createContext(defaultState);
export const DataDispatchContext = /*#__PURE__*/React.createContext(undefined);
export const DataProvider = ({
  children,
  initialTopics
}) => {
  const {
    i18n
  } = useTranslation();
  const axios = useAxios();
  const paths = usePath();
  const snackbarDispatch = useSnackbarDispatch();
  const analyticsDispatch = useAnalyticsDispatch();
  const dialogDispatch = useDialogDispatch();
  const routes = useRoutes();
  const {
    baseUrl
  } = useVideoConfig();
  const {
    personalizationSupported,
    loginSupported,
    favoritesSupported,
    myVideosSupported
  } = useVideoConfig();
  const middlewares = [fetchMiddleware(axios), loginSupported ? rtsLoginDialogMiddleware(dialogDispatch) : personalizationDialogMiddleware(dialogDispatch), loginSupported ? rtsWatchLaterStorageMiddleware : watchLaterStorageMiddleware, favoritesSupported && favoriteStorageMiddleware, personalizationStorageMiddleware, snackbarMiddleware(snackbarDispatch, i18n, myVideosSupported, paths), analyticsMiddleware(analyticsDispatch, routes, baseUrl)].filter(middleware => middleware);
  const useCustomReducer = createReducer(...middlewares);
  const isHydrated = useHydrated();
  const initialState = createInitialState(initialTopics);
  const [state, dispatch] = useCustomReducer(reducer, initialState);
  useRtsLogin(state, dispatch);
  useEffect(() => {
    if (!isHydrated) {
      return;
    }
    const settings = personalizationSupported ? personalizationSettings.get() : personalizationSettings.set({
      isActive: false,
      showHeaderMessage: false
    });
    dispatch({
      type: 'initialize-personalization-data',
      settings,
      favoriteShowIds: loginSupported ? [] : favoriteShowsStorage.get(),
      favoriteTopicIds: loginSupported ? [] : favoriteTopicsStorage.get(),
      watchLaterUrns: loginSupported ? [] : watchLaterStorage.get(),
      showSubtitle: loginSupported || subtitleStorage.get(),
      showFavoriteGuide: favoriteGuideStorage.get(),
      channelHistory: channelHistoryStorage.get()
    });
    const userId = userIdStorage.get();
    if (userId) {
      dispatch({
        type: 'load-user-id-success',
        userId
      });
    }
  }, [dispatch, loginSupported, personalizationSupported, isHydrated]);
  return /*#__PURE__*/_jsx(DataStateContext.Provider, {
    value: state,
    children: /*#__PURE__*/_jsx(DataDispatchContext.Provider, {
      value: dispatch,
      children: children
    })
  });
};
export const useDataDispatch = () => {
  const context = useContext(DataDispatchContext);
  if (context === undefined) {
    throw new Error('useDataDispatch must be used within a DataProvider');
  }
  return context;
};
export const useDataState = () => {
  const state = useContext(DataStateContext);
  if (state === undefined) {
    throw new Error('useDataState must be used within a DataProvider');
  }
  return state;
};
export const usePersonalizationSettings = () => useDataState().personalization;
export const useFavoriteGuideState = () => useDataState().favoriteGuideState;
export const useTopicState = () => useDataState().topicState;
export const useUserIdState = () => useDataState().userIdState;
export const useShowState = () => useDataState().showState;
export const useWatchLaterState = () => useDataState().watchLaterState;
export const useSubtitleState = () => useDataState().subtitleState;
export const useLoginState = () => useDataState().loginState;